import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  CreditCard, Euro, TrendingUp, TrendingDown, Clock, 
  CheckCircle, XCircle, AlertCircle, Eye, Filter, 
  Download, Calendar, Search, ChevronRight, FileText
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { companyApi } from '../../../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  paid: { label: 'Payé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  failed: { label: 'Échoué', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expiré', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
};

export function PaymentsPage() {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [totals, setTotals] = useState({});
  const [statusCounts, setStatusCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        companyApi.getPayments({ status: filterStatus !== 'all' ? filterStatus : undefined }),
        companyApi.getPaymentStats()
      ]);
      
      setTransactions(paymentsRes.data.transactions || []);
      setTotals(paymentsRes.data.totals || {});
      setStatusCounts(paymentsRes.data.status_counts || {});
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Erreur lors du chargement des paiements');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterStatus]);

  const openDetail = async (transaction) => {
    try {
      const response = await companyApi.getPaymentDetails(transaction.id);
      setSelectedTransaction(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      t.client_name?.toLowerCase().includes(query) ||
      t.client_email?.toLowerCase().includes(query) ||
      t.services?.some(s => s.toLowerCase().includes(query))
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Paiements</h1>
        <p className="text-muted-foreground">
          Suivi des paiements reçus via vos widgets de réservation
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Chiffre d'affaires total</p>
                <p className="text-2xl font-bold">{stats?.total_revenue?.toFixed(2) || '0.00'}€</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Euro className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ce mois-ci</p>
                <p className="text-2xl font-bold">{stats?.month_revenue?.toFixed(2) || '0.00'}€</p>
                {stats?.growth_percent !== 0 && (
                  <div className={`flex items-center text-xs mt-1 ${stats?.growth_percent > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {stats?.growth_percent > 0 ? (
                      <TrendingUp className="w-3 h-3 mr-1" />
                    ) : (
                      <TrendingDown className="w-3 h-3 mr-1" />
                    )}
                    {stats?.growth_percent > 0 ? '+' : ''}{stats?.growth_percent}% vs mois dernier
                  </div>
                )}
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-2xl font-bold">{stats?.total_transactions || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Panier moyen: {stats?.average_transaction?.toFixed(2) || '0.00'}€
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Statuts</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    {statusCounts.paid || 0} payés
                  </Badge>
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                    {statusCounts.pending || 0} en attente
                  </Badge>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par client, email ou service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="paid">Payés</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="failed">Échoués</SelectItem>
                  <SelectItem value="expired">Expirés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transactions récentes</CardTitle>
          <CardDescription>
            Liste de tous les paiements effectués via vos widgets
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun paiement trouvé</p>
              {filterStatus !== 'all' && (
                <Button 
                  variant="link" 
                  onClick={() => setFilterStatus('all')}
                  className="mt-2"
                >
                  Afficher tous les paiements
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Service(s)</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Montant</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => {
                    const statusConfig = STATUS_CONFIG[transaction.payment_status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <tr 
                        key={transaction.id} 
                        className="border-b hover:bg-muted/50 transition-colors"
                        data-testid={`payment-row-${transaction.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="text-sm">{formatDate(transaction.created_at)}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{transaction.client_name}</p>
                            <p className="text-sm text-muted-foreground">{transaction.client_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm">
                            {transaction.services?.join(', ') || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-bold">{transaction.amount?.toFixed(2)}€</p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {transaction.payment_status === 'paid' && transaction.invoice_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const token = localStorage.getItem('token');
                                  window.open(`${API_URL}/api/company/client-invoices/${transaction.invoice_id}/download?token=${token}`, '_blank');
                                }}
                                title="Télécharger la facture"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetail(transaction)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Détails
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails du paiement</DialogTitle>
            <DialogDescription>
              Référence: {selectedTransaction?.transaction?.id?.slice(0, 8).toUpperCase()}
            </DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                {(() => {
                  const statusConfig = STATUS_CONFIG[selectedTransaction.transaction?.payment_status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <Badge className={`${statusConfig.color} text-base py-1 px-3`}>
                      <StatusIcon className="w-4 h-4 mr-2" />
                      {statusConfig.label}
                    </Badge>
                  );
                })()}
                <span className="text-2xl font-bold">
                  {selectedTransaction.transaction?.amount?.toFixed(2)}€
                </span>
              </div>

              {/* Client Info */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">Client</h4>
                <p className="font-medium">{selectedTransaction.transaction?.client_name}</p>
                <p className="text-sm">{selectedTransaction.transaction?.client_email}</p>
                {selectedTransaction.transaction?.client_phone && (
                  <p className="text-sm">{selectedTransaction.transaction?.client_phone}</p>
                )}
              </div>

              {/* Services */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-muted-foreground">Service(s)</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedTransaction.transaction?.services?.map((service, idx) => (
                    <Badge key={idx} variant="outline">{service}</Badge>
                  ))}
                </div>
              </div>

              {/* Booking Info */}
              {selectedTransaction.booking && (
                <div className="bg-green-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-sm text-green-700">Réservation créée</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Date:</span>
                      <p className="font-medium">{selectedTransaction.booking.date}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Horaire:</span>
                      <p className="font-medium">
                        {selectedTransaction.booking.start_time} - {selectedTransaction.booking.end_time}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice Download */}
              {selectedTransaction.transaction?.payment_status === 'paid' && selectedTransaction.transaction?.invoice_id && (
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => {
                    const token = localStorage.getItem('token');
                    window.open(`${API_URL}/api/company/client-invoices/${selectedTransaction.transaction.invoice_id}/download?token=${token}`, '_blank');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger la facture
                </Button>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Créé le:</span>
                  <p>{formatDate(selectedTransaction.transaction?.created_at)}</p>
                </div>
                {selectedTransaction.transaction?.paid_at && (
                  <div>
                    <span className="text-muted-foreground">Payé le:</span>
                    <p>{formatDate(selectedTransaction.transaction?.paid_at)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
